import { $, html, View } from 'rune-ts';
import { ARTICLE_TYPES } from '@modules/MShop/App/Store/Community/S/Constant/community';
import klass from '../FanLetterList.module.scss';
import { getResizedUrl } from '../../../../../shared/util/image';
import { font, staticFont } from '../../../../../shared/typography';
import {
  FanLetterFormFileChangeEvent,
  FanLetterFormSubmitEvent,
  FanLetterFormTextAreaFocusEvent,
  FanLetterFormTextAreaInputEvent,
} from '../events/FanLetterEvents';
import { ButtonAction } from '../../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { FanLetterFormTextAreaView } from './FanLetterFormTextAreaView';
import { FanLetterFormUploadingImagesView } from './FanLetterFormUploadingImagesView';
import { goLogin } from '../../../../../shared/util/goLogin';
import { CreatorTypeS } from '../../../../../features/Creator/outbound/share';
import { default_creator_img } from '@services/MarppleShop/features/Creator/constant';
import { FanLetterListData } from '@services/MarppleShop/renderApp/components/cells/FanLetterList/FanLetterListView.ts';

interface FanLetterFormData {
  creator: CreatorTypeS.CreatorMainProfile;
  is_login: boolean;
  is_mobile: boolean;
  list_type: FanLetterListData['list_type'];
}

class FanLetterFormSubmitButtonAction extends ButtonAction {
  public switchLabelOption(small_mode: boolean) {
    this.data.label = small_mode ? smallModeBtnSvg() : ET('community::input');
    this.redraw();
  }

  public switchTypeOption(is_disabled: boolean) {
    this.setTypeOption(is_disabled ? 'disabled' : 'blue');
  }

  public isDisabled() {
    return this.getTypeOption() == 'disabled';
  }
}

export class FanLetterFormView extends View<FanLetterFormData> {
  private static MAX_TEXT_COUNT = 500;
  private static MAX_IMAGE_COUNT = 6;
  private static UPLOAD_INPUT_IMAGE =
    '//s3.marpple.co/files/u_29089/2025/3/original/8e87f7581aa31a17c0866b107edc4c44ee466bc81.svg';

  private small_mode = this.data.is_mobile;

  private form_mode = this._getMode();

  private uploadingImagesView = new FanLetterFormUploadingImagesView({ files: [] });

  private textAreaView = new FanLetterFormTextAreaView({
    max_count: FanLetterFormView.MAX_TEXT_COUNT,
    is_login: this.data.is_login,
    is_mobile: this.data.is_mobile,
    small_mode: this.small_mode,
    mode: this._getMode(),
  });

  private submitButtonView = new FanLetterFormSubmitButtonAction({
    label: this.small_mode ? smallModeBtnSvg() : ET('community::input'),
    klass: klass.submit_button,
    type: this.small_mode ? 'blue' : 'disabled',
    size: 'medium',
  });

  private _getMode() {
    if (this.data.is_mobile) {
      return 'small';
    }
    if (this.data.list_type == 'masonry') {
      return 'large';
    }
    return 'medium';
  }

  private _modeTo() {
    if (this.form_mode == 'small') {
      return { klass: klass.small_mode_form, font: font('14_bold') };
    }
    if (this.form_mode == 'large') {
      return { klass: klass.large_mode_form, font: font('16_bold') };
    }
    return { klass: klass.medium_mode_form, font: font('14_bold') };
  }

  private async _getSubmitLabel() {
    /* ET 가 아직 promise 일 수 있다. */
    return this.small_mode ? smallModeBtnSvg() : ET('community::input');
  }

  protected override template() {
    const { creator } = this.data;
    if (this.small_mode) {
      return html`<div class="${klass.fan_letters_form_small}">
        ${this.textAreaView}${this.submitButtonView}
      </div>`;
    } else {
      const form_mode = this._modeTo();
      return html` <div class="${klass.fan_letters_form} ${form_mode.klass}">
        <div class="${klass.form_top}">
          <div class="${klass.fan_letters_form_creator_profile}">
            <img
              class="${klass.fan_letters_form_creator_profile_img}"
              src="${getResizedUrl({
                url: creator.thumbnail_img || default_creator_img,
                width: 180 * 2,
                format: 'webp',
                quality: 80,
              })}"
              alt=""
            />
            <div class="${klass.fan_letters_form_creator_profile_right} ${form_mode.font}">
              <div class="${klass.submit_to}">To.</div>
              <div class="${klass.creator_name}">${html.preventEscape(creator.name)}</div>
            </div>
          </div>
          ${this.textAreaView}${this.uploadingImagesView}
        </div>
        <div class="${klass.fan_letters_bottom}">
          <div class="${klass.fan_letters_bottom_left}">
            ${this.data.is_login
              ? html`<input
                  type="file"
                  class="${klass.upload_input}"
                  id="fan_letters_form_images_upload"
                  multiple
                  accept=".jpg,.png,.jpeg,.gif"
                />`
              : ''}
            <label class="${klass.upload_label}" for="fan_letters_form_images_upload">
              <img class="${klass.upload_img}" src="${FanLetterFormView.UPLOAD_INPUT_IMAGE}" alt="" />
            </label>
          </div>

          <div class="${klass.fan_letters_bottom_right}">
            <div class="${klass.total_length} ${staticFont('unica_14_medium')}">
              <span class="${klass.current_length}">0</span><span>/</span
              ><span>${FanLetterFormView.MAX_TEXT_COUNT}</span>
            </div>
            ${this.submitButtonView}
          </div>
        </div>
      </div>`;
    }
  }

  private _setCurrentLength$(value_length: number) {
    const current_length$ = this.element().querySelector(`.${klass.current_length}`);
    if (current_length$) {
      current_length$.innerHTML = value_length.toString();
    }
    return this;
  }

  private _getFiles$(): FileList {
    const input_file = this.getInputFile$();
    if (!input_file?.files?.length) {
      return new DataTransfer().files;
    }
    return input_file.files;
  }

  public getUploadingFanLetterImagesView() {
    return this.uploadingImagesView;
  }

  public getFanLetterTextAreaView() {
    return this.textAreaView;
  }

  public getInputFile$(): HTMLInputElement | null {
    return this.element().querySelector<HTMLInputElement>(`.${klass.upload_input}`);
  }

  public initInputFile$() {
    const input_file = this.getInputFile$();
    if (input_file) {
      input_file.value = '';
    }
    return this;
  }

  protected override async onRender() {
    this.submitButtonView.setLabel(await this._getSubmitLabel());

    /* 로그인 */ if (this.data.is_login) {
      if (this.data.is_mobile) {
        this.addEventListener('click', async () => {
          if (!this.small_mode) {
            return;
          }
          this.small_mode = false;
          this.textAreaView.setSmallMode(false);
          this.redraw();
          this.textAreaView.element().focus();
          this.submitButtonView.switchTypeOption(true);
          this.submitButtonView.switchLabelOption(false);
        });
      }
      this.delegate('input', `.${klass.upload_input}`, async () => {
        const files = this._getFiles$();

        if (!files?.length) {
          return;
        }

        this.dispatchEvent(FanLetterFormFileChangeEvent, {
          bubbles: true,
          detail: {
            is_over_max_count:
              this.uploadingImagesView.getFiles().length + files?.length > FanLetterFormView.MAX_IMAGE_COUNT,
          },
        });
      });

      this.delegate('click', FanLetterFormSubmitButtonAction, async () => {
        if (this.submitButtonView.isDisabled() || this.small_mode) {
          return;
        }

        const type =
          this.uploadingImagesView.getFiles().length > 0
            ? ARTICLE_TYPES.community_photo
            : ARTICLE_TYPES.community_text;

        this.dispatchEvent(FanLetterFormSubmitEvent, {
          bubbles: true,
          detail: {
            type,
          },
        });
      });

      this.delegate(FanLetterFormTextAreaInputEvent, FanLetterFormTextAreaView, async (e) => {
        const { value_length, is_over_min_count: is_not_disabled } = e.detail;
        this._setCurrentLength$(value_length);
        this.submitButtonView.switchTypeOption(!is_not_disabled);
      });

      this.delegate(FanLetterFormTextAreaFocusEvent, FanLetterFormTextAreaView, (e) => {
        $(this.element()).setAttribute('data-focus', e.detail.focus);
      });
    } /* 로그아웃 */ else {
      this.addEventListener('click', async () => {
        return goLogin();
      });
    }
  }
}

function smallModeBtnSvg() {
  return html`<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="Write" clip-path="url(#clip0_2373_200294)">
      <path
        id="Vector"
        d="M14.0599 5.06949L10.9225 1.92732C10.4862 1.49037 9.72268 1.49037 9.28639 1.92732L5.13521 6.08475L1.94003 9.28475C1.72188 9.50322 1.59998 9.79238 1.59998 10.1008V13.243C1.59998 13.8856 2.11967 14.3996 2.75486 14.3996H5.89231C6.20028 14.3996 6.489 14.2775 6.71356 14.059L14.0599 6.70162C14.2781 6.48314 14.4 6.19399 14.4 5.87913C14.4 5.56427 14.2781 5.28154 14.0599 5.05664V5.06949ZM11.9747 7.20282L8.78594 4.00925L10.0756 2.71768H10.1269L13.2643 5.91126L11.9747 7.20282Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_2373_200294">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg> `;
}
